import { reactive, shallowRef } from 'vue';
import { pinia } from '@/stores';
import { createMyVuetify, resolveVuetifyLocale } from '@/plugins/vuetify';
import { createZoneApp, ensureVuetifyStyles } from '@/mount/zoneMount';
import { iframeDataBridge } from '@/iframe/iframeDataBridge';
import { log } from '@/utils/core/vueLogger.js';
import { nativeFieldType, readNativeField } from '@/utils/legacy/legacyNativeField';
import EcosFieldHost from '@/components/sales/EcosFieldHost.vue';

const HOST_ID = 'vue-ecos-field-host';
const PLACEHOLDER_CLASS = 'ecos-list-field-mount';
const PLACEHOLDER_SELECTOR = `.${PLACEHOLDER_CLASS}[data-ecos-for]`;
const ANCHOR_CLASS = 'ecos-list-field-anchor';
const SWEEP_INTERVAL_MS = 1000;

let app = null;
let hostEl = null;
let doc = null;
let observer = null;
let sweepTimer = null;
let rafPending = false;
let uidCounter = 0;

/** @type {Map<string, object>} keyed by the placeholder's `data-ecos-for` (= the native element's id) */
const registry = new Map();
const fields = shallowRef([]);

function sameArray(a, b) {
	return a.length === b.length && a.every((value, index) => value === b[index]);
}

function sameOptions(a, b) {
	return a.length === b.length && a.every((option, index) => option.id === b[index]?.id && option.label === b[index]?.label);
}

/** Pulls the descriptor's reactive fields up to date with the native element, no-op if unchanged. */
function syncFromNative(field) {
	const state = readNativeField(field.nativeEl);
	if (!state) return;

	if (!sameOptions(field.options, state.options)) field.options = state.options;
	if (!sameArray(field.selected, state.selected)) field.selected = state.selected;
	if (field.disabled !== state.disabled) field.disabled = state.disabled;
	if (field.mandatory !== state.mandatory) field.mandatory = state.mandatory;
}

/** Config carried on the placeholder itself rather than the native element — see the contract. */
function readPlaceholderConfig(placeholder) {
	const maxChips = Number(placeholder.dataset.ecosMaxChips);
	return {
		maxDisplayedChips: Number.isFinite(maxChips) && maxChips > 0 ? maxChips : 2,
		label: placeholder.dataset.ecosLabel || '',
	};
}

function createDescriptor(id, nativeEl, placeholder) {
	const anchor = doc.createElement('div');
	anchor.className = ANCHOR_CLASS;

	const field = reactive({
		uid: ++uidCounter,
		id,
		anchor,
		nativeEl,
		type: nativeFieldType(nativeEl),
		options: [],
		selected: [],
		disabled: false,
		mandatory: false,
		menuOpen: false,
		...readPlaceholderConfig(placeholder),
	});

	syncFromNative(field);
	return field;
}

/** Idempotent, O(placeholders): re-points existing descriptors, moves anchors, drops orphans. */
function rescan() {
	if (!doc) return;

	const seen = new Set();
	let dirty = false;

	for (const placeholder of doc.querySelectorAll(PLACEHOLDER_SELECTOR)) {
		const id = placeholder.getAttribute('data-ecos-for');
		if (!id) continue;

		const nativeEl = doc.getElementById(id);
		if (!nativeEl || nativeFieldType(nativeEl) === null) continue;

		seen.add(id);

		const existing = registry.get(id);
		if (existing) {
			existing.nativeEl = nativeEl;
			Object.assign(existing, readPlaceholderConfig(placeholder));
			// Knockout replaced the placeholder wrapping this id — move the anchor into the new
			// one instead of remounting, so the component instance (and any open menu) survives.
			if (existing.anchor.parentNode !== placeholder) placeholder.appendChild(existing.anchor);
			syncFromNative(existing);
			continue;
		}

		const field = createDescriptor(id, nativeEl, placeholder);
		placeholder.appendChild(field.anchor);
		registry.set(id, field);
		dirty = true;
	}

	for (const [id, field] of registry) {
		if (!seen.has(id) || !field.anchor.isConnected) {
			registry.delete(id);
			dirty = true;
		}
	}

	if (dirty) fields.value = [...registry.values()];
}

function scheduleRescan() {
	if (rafPending || !doc) return;
	rafPending = true;

	const raf = doc.defaultView?.requestAnimationFrame?.bind(doc.defaultView) ?? requestAnimationFrame;
	raf(() => {
		rafPending = false;
		rescan();
	});
}

/** Cheap pre-filter so unrelated legacy DOM churn never schedules a rescan. */
function touchesAField(record) {
	if (record.type !== 'childList') return false;

	const matches = (node) => node.nodeType === 1 && (node.classList?.contains(PLACEHOLDER_CLASS) || node.querySelector?.(`.${PLACEHOLDER_CLASS}`));

	return [...record.addedNodes].some(matches) || [...record.removedNodes].some(matches);
}

function sweep() {
	let dirty = false;

	for (const [id, field] of registry) {
		if (!field.anchor.isConnected) {
			registry.delete(id);
			dirty = true;
			continue;
		}
		if (field.menuOpen) continue;
		syncFromNative(field);
	}

	if (dirty) fields.value = [...registry.values()];
}

/**
 * Mounts the host app into the current legacy iframe document. Idempotent — safe to call from
 * every legacy hook that (re)wires the relevé's attribute fields.
 */
export function mountEcosFieldHost() {
	const iframeDoc = iframeDataBridge.getIframeWindow()?.document;
	if (!iframeDoc) return;

	if (app && doc === iframeDoc) {
		rescan();
		return;
	}

	// A stale mount from a previous iframe load — tear it down before pinning the new document.
	if (app) unmountEcosFieldHost();

	ensureVuetifyStyles();

	doc = iframeDoc;
	hostEl = doc.createElement('div');
	hostEl.id = HOST_ID;
	// Nothing renders here directly — every field leaves through its own Teleport.
	hostEl.style.display = 'none';
	doc.body.appendChild(hostEl);

	app = createZoneApp(EcosFieldHost);
	// Provided rather than passed as a root prop: createApp(Component, rootProps) hands the child
	// the raw values ONCE — a shallowRef passed that way arrives as the Ref object itself (fails
	// prop validation) and never reflects later reassignments. provide() keeps the live binding.
	app.provide('ecosFields', fields);
	app.provide('overlayTarget', doc.getElementById('vue-overlay-root'));
	app.use(pinia);
	app.use(createMyVuetify(resolveVuetifyLocale(iframeDataBridge.getCurrentUser()?.language)));
	app.mount(hostEl);

	// Deterministic reconciliation hook the legacy calls at its own sync points (wizard↔list sync,
	// checkForReadonly) — the MutationObserver below only catches placeholders appearing/disappearing,
	// not the jQuery .prop()/.val() writes those two paths use.
	if (doc.defaultView?.externis) {
		doc.defaultView.externis.ecosListField = { refresh: rescan };
	}

	observer = new MutationObserver((records) => {
		if (records.some(touchesAField)) scheduleRescan();
	});
	observer.observe(doc.body, { childList: true, subtree: true });

	sweepTimer = doc.defaultView.setInterval(sweep, SWEEP_INTERVAL_MS);

	rescan();
	log('info', 'ecosFieldHost', '✅ eCOS list field host mounted');
}

export function unmountEcosFieldHost() {
	if (observer) {
		observer.disconnect();
		observer = null;
	}
	if (sweepTimer) {
		(doc?.defaultView ?? window).clearInterval(sweepTimer);
		sweepTimer = null;
	}
	if (doc?.defaultView?.externis?.ecosListField) {
		delete doc.defaultView.externis.ecosListField;
	}
	if (app) {
		app.unmount();
		app = null;
	}
	if (hostEl?.parentNode) {
		hostEl.parentNode.removeChild(hostEl);
	}

	hostEl = null;
	doc = null;
	rafPending = false;
	registry.clear();
	fields.value = [];
}
